import React, { useLayoutEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useHistory, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { APICategory } from "../../../../models/Entities";
import {
  getIntegrationCategoryList,
  categoryDisplayName,
  isBetaCategory,
} from "../../../../models/Helpers";
import { commonModelsConfigurationPathForIndividualCategory } from "../../../../router/RouterUtils";
import useAppContext from "../../../context/useAppContext";
import RedactUnmappedDataToggle from "./common-model-scopes/components/RedactUnmappedDataToggle";
import PortalPageHeaderWrapper from "../../../portal/PortalPageHeaderWrapper";
import { ButtonVariant, Chips, Menu, MenuItem } from "@merge-api/merge-javascript-shared";

const PAGE_TITLE = "Common Model Scopes";

// Category selector label, with a plain-text "(Beta)" suffix for beta categories. The category
// chips/menu can't render a Badge inside a chip, so beta status is shown as text (e.g. "Chat (Beta)").
const categorySelectorLabel = (category: APICategory): string =>
  isBetaCategory(category)
    ? `${categoryDisplayName(category)} (Beta)`
    : categoryDisplayName(category);

const ConfigurationCommonModelsPageWrapper = ({
  children,
}: Pick<React.ComponentProps<"div">, "children">) => {
  const { user } = useAppContext();
  const location = useLocation();
  const history = useHistory();

  const selectorRef = useRef<HTMLDivElement>(null);
  const chipsRef = useRef<HTMLDivElement>(null);
  const [showCategoryMenu, setShowCategoryMenu] = useState(true);

  // Measure before paint to avoid flashing the wrong layout, then track container and content resizing.
  useLayoutEffect(() => {
    const selector = selectorRef.current;
    const chips = chipsRef.current;
    if (!selector || !chips) return;

    const updateCategoryMenu = () => {
      setShowCategoryMenu(chips.getBoundingClientRect().width > selector.clientWidth);
    };
    updateCategoryMenu();

    const observer = new ResizeObserver(updateCategoryMenu);
    observer.observe(selector);
    observer.observe(chips);
    return () => observer.disconnect();
  }, []);

  const categories = getIntegrationCategoryList(user);
  const activeCategory = categories.find((category) =>
    location.pathname.startsWith(commonModelsConfigurationPathForIndividualCategory(category)),
  );

  return (
    <PortalPageHeaderWrapper
      title={PAGE_TITLE}
      subtitle="Manage which data you sync via Merge's Unified API. Models with more than 90 days of inactivity are automatically disabled."
    >
      <div className="flex flex-col gap-y-5">
        <div className="flex flex-row items-center justify-between gap-x-5">
          <div ref={selectorRef} className="relative min-w-0 flex-1">
            <div
              aria-hidden={showCategoryMenu || undefined}
              className={clsx(
                showCategoryMenu && "invisible absolute inset-x-0 top-0 overflow-hidden",
              )}
            >
              <div ref={chipsRef} className="w-max whitespace-nowrap">
                <Chips
                  size="sm"
                  options={categories.map((category) => ({
                    value: category,
                    text: categorySelectorLabel(category),
                    selected: category === activeCategory,
                    disabled: false,
                    onClick: (value) =>
                      history.push(
                        commonModelsConfigurationPathForIndividualCategory(value as APICategory),
                      ),
                  }))}
                />
              </div>
            </div>
            {showCategoryMenu && (
              <Menu
                ButtonProps={{
                  variant: ButtonVariant.SecondaryGray,
                  "aria-label": "Select category",
                  children: activeCategory
                    ? categorySelectorLabel(activeCategory)
                    : "Select category",
                  rightIcon: <ChevronDown size={16} />,
                }}
                menuPlacement="bottom-start"
              >
                {categories.map((category) => (
                  <MenuItem
                    key={category}
                    onClick={() =>
                      history.push(commonModelsConfigurationPathForIndividualCategory(category))
                    }
                    className={clsx(category === activeCategory && "bg-gray-10 font-semibold")}
                  >
                    {categorySelectorLabel(category)}
                  </MenuItem>
                ))}
              </Menu>
            )}
          </div>
          <RedactUnmappedDataToggle />
        </div>
        {children}
      </div>
    </PortalPageHeaderWrapper>
  );
};

export default ConfigurationCommonModelsPageWrapper;
